<template>
    <div
        class="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <div class="flex flex-col gap-2.5 px-[18px] pt-3.5">
            <div class="flex items-center justify-between">
                <span
                    class="text-[15px] font-semibold text-zinc-950 dark:text-white"
                >
                    Contexto do vendedor
                </span>
                <span class="text-[12.5px] text-zinc-500 dark:text-gray-400">
                    Resumo 360°
                </span>
            </div>
            <div
                class="flex gap-0.5 overflow-x-auto border-b border-zinc-200 dark:border-gray-700"
            >
                <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    type="button"
                    class="-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 py-2 text-[13px] transition-colors"
                    :class="
                        activeTab === tab.id
                            ? 'border-zinc-900 font-medium text-zinc-950 dark:border-white dark:text-white'
                            : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:text-gray-400 dark:hover:text-gray-200'
                    "
                    @click="activeTab = tab.id"
                >
                    {{ tab.label }}
                    <span
                        v-if="tab.flag"
                        class="h-1.5 w-1.5 rounded-full bg-amber-600"
                    ></span>
                </button>
            </div>
        </div>

        <div class="p-[18px]">
            <review-summary-360
                v-if="activeTab === 'resumo'"
                :review="review"
                :is-same-day-urgent="isSameDayUrgent"
            />

            <div v-else-if="activeTab === 'pagamentos'" class="space-y-4">
                <bin-usage-card :cashout="review.cashout" />
                <payment-method-card :cashout="review.cashout" />
            </div>

            <div v-else-if="activeTab === 'comissoes'">
                <div v-if="commissions.length" class="flex flex-col gap-1.5">
                    <p
                        class="mb-1 text-[12.5px] text-zinc-500 dark:text-gray-400"
                    >
                        Concentração por origem, em relação ao valor do saque.
                    </p>
                    <div
                        v-for="commission in commissions"
                        :key="commission.type"
                        class="flex items-center justify-between rounded-lg border border-zinc-100 px-3 py-2 dark:border-gray-700"
                    >
                        <div class="flex items-center gap-2">
                            <span
                                class="rounded px-1.5 py-0.5 text-xs font-medium"
                                :class="getCommissionTypeColor(commission.type)"
                            >
                                {{ formatCommissionType(commission.type) }}
                            </span>
                            <span
                                class="font-mono text-xs"
                                :class="
                                    commission.share > 50
                                        ? 'text-amber-700 dark:text-amber-400'
                                        : 'text-zinc-500 dark:text-gray-400'
                                "
                            >
                                {{ commission.share.toFixed(1) }}%
                            </span>
                        </div>
                        <span
                            class="text-sm font-semibold tabular-nums text-zinc-950 dark:text-white"
                        >
                            {{ $root.formatCurrency(commission.amount) }}
                        </span>
                    </div>
                </div>
                <p
                    v-else
                    class="py-4 text-center text-[13px] text-zinc-500 dark:text-gray-400"
                >
                    {{
                        payableDetails
                            ? "Nenhuma informação de comissão disponível."
                            : "Carregando comissões…"
                    }}
                </p>
            </div>

            <cashout-payables
                v-else-if="activeTab === 'recebiveis'"
                :cashout="review.cashout"
                :products="true"
                :payable-details="payableDetails"
            />
        </div>
    </div>
</template>

<script>
import ReviewSummary360 from "./ReviewSummary360.vue";
import BinUsageCard from "./BinUsageCard.vue";
import PaymentMethodCard from "./PaymentMethodCard.vue";
import CashoutPayables from "../../../views/finnancial/cashout/CashoutPayables.vue";
import CommissionType from "../../../mixins/CommissionType.vue";

export default {
    name: "SellerContextCard",
    components: {
        ReviewSummary360,
        BinUsageCard,
        PaymentMethodCard,
        CashoutPayables,
    },
    mixins: [CommissionType],
    props: {
        review: {
            type: Object,
            required: true,
        },
        payableDetails: {
            type: Object,
            default: null,
        },
        isSameDayUrgent: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeTab: "resumo",
        };
    },
    computed: {
        commissions() {
            const types = this.payableDetails?.audit?.commission_types;
            const baseAmount = Number(this.review?.cashout?.amount) || 0;

            if (!types) {
                return [];
            }

            return Object.values(types)
                .map((commission) => {
                    const share = baseAmount
                        ? (commission.amount / baseAmount) * 100
                        : 0;

                    return {
                        type: commission.type,
                        amount: commission.amount,
                        share: Number.isFinite(share) ? share : 0,
                    };
                })
                .sort((a, b) => b.amount - a.amount);
        },
        tabs() {
            return [
                { id: "resumo", label: "Resumo" },
                { id: "pagamentos", label: "Pagamentos" },
                {
                    id: "comissoes",
                    label: "Comissões",
                    flag: this.commissions.some(
                        (commission) => commission.share > 50,
                    ),
                },
                { id: "recebiveis", label: "Recebíveis" },
            ];
        },
    },
    watch: {
        "review.id"() {
            this.activeTab = "resumo";
        },
    },
};
</script>
